import { useState, useEffect, useCallback } from 'react';

interface UseContentEditablePayload {
    initialValue: string;
    onSave: (content: string) => void;
}

const useContentEditable = (payload: UseContentEditablePayload) => {
    const { initialValue, onSave } = payload;

    const [isEditMode, setIsEditMode] = useState(false);
    const [draft, setDraft] = useState(initialValue);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Sync draft with external initialValue changes when not editing
    useEffect(() => {
        if (isEditMode) return; // Don't sync if in edit mode

        setDraft(initialValue);
        setHasUnsavedChanges(false);
    }, [initialValue, isEditMode]);

    const isDraftValid = useCallback(() => {
        const trimmedDraft = draft.trim();
        if (!trimmedDraft) return false;
        return draft !== initialValue;
    }, [draft, initialValue]);

    const enterEditMode = useCallback(() => {
        setIsEditMode(true);
        setDraft(initialValue); // Reset draft to current value when entering edit mode
        setHasUnsavedChanges(false);
    }, [initialValue]);

    const exitEditMode = useCallback(() => {
        setIsEditMode(false);
    }, []);

    const handleChange = useCallback((newContent: string) => {
        setDraft(newContent);
        setHasUnsavedChanges(true);
    }, []);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') return cancel();
        },
        [hasUnsavedChanges],
    );

    const cancel = useCallback(() => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to discard them?');
            if (confirmed) handleCacnel();
        } else exitEditMode();
    }, [hasUnsavedChanges]);

    const handleBlur = useCallback(() => {
        if (!hasUnsavedChanges) exitEditMode();
    }, [hasUnsavedChanges]);

    const save = useCallback(() => {
        onSave(draft.trim());
        setHasUnsavedChanges(false);
    }, [draft]);

    const handleCacnel = useCallback(() => {
        exitEditMode();
        setDraft(initialValue);
        setHasUnsavedChanges(false);
    }, [initialValue]);

    return {
        isEditMode,
        draft,
        hasUnsavedChanges,
        enterEditMode,
        exitEditMode,
        handleChange,
        handleKeyDown,
        handleBlur,
        isDraftValid,
        save,
        cancel,
    };
};

export default useContentEditable;
